import React, { createContext, useState, useContext, useEffect } from 'react';
import * as api from '../services/api';

const AuthContext = createContext(null);

const asBool = (value) => value === true || value === 'true' || value === 1 || value === '1';

const persistRoles = (companyAdmin, systemAdmin) => {
  localStorage.setItem('isCompanyAdmin', String(!!companyAdmin));
  localStorage.setItem('isSystemAdmin', String(!!systemAdmin));
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  useEffect(() => {
    // Check if user is already logged in (from localStorage)
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const storedCompanyAdmin = localStorage.getItem('isCompanyAdmin');
    const storedSystemAdmin = localStorage.getItem('isSystemAdmin');
    
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Validate that parsedUser is an object with expected properties
        if (parsedUser && typeof parsedUser === 'object' && parsedUser.id) {
          const companyAdmin =
            asBool(storedCompanyAdmin) || asBool(parsedUser.is_company_admin);
          const systemAdmin =
            asBool(storedSystemAdmin) || asBool(parsedUser.is_system_admin);
          setUser(parsedUser);
          setIsAuthenticated(true);
          setIsCompanyAdmin(companyAdmin);
          setIsSystemAdmin(systemAdmin);
          persistRoles(companyAdmin, systemAdmin);
        } else {
          // Invalid user data, clear localStorage
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('isCompanyAdmin');
          localStorage.removeItem('isSystemAdmin');
        }
      } catch (error) {
        // Invalid JSON in localStorage, clear it
        console.error('Invalid user data in localStorage, clearing...');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('isCompanyAdmin');
        localStorage.removeItem('isSystemAdmin');
      }
    }
    setIsLoading(false); // Done loading
  }, []);

  const signup = async (userData) => {
    try {
      const response = await api.signup(userData);
      const companyAdmin = asBool(response.is_company_admin) || asBool(response.user?.is_company_admin);
      const systemAdmin = asBool(response.is_system_admin) || asBool(response.user?.is_system_admin);
      setUser(response.user);
      setIsAuthenticated(true);
      setIsCompanyAdmin(companyAdmin);
      setIsSystemAdmin(systemAdmin);
      
      // Save to localStorage
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('token', response.token);
      persistRoles(companyAdmin, systemAdmin);
      
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.login({ email, password });
      
      // If 2FA is required, return without setting auth state
      if (response.two_factor_required) {
        return { success: true, data: response };
      }
      
      const companyAdmin = asBool(response.is_company_admin) || asBool(response.user?.is_company_admin);
      const systemAdmin = asBool(response.is_system_admin) || asBool(response.user?.is_system_admin);
      setUser(response.user);
      setIsAuthenticated(true);
      setIsCompanyAdmin(companyAdmin);
      setIsSystemAdmin(systemAdmin);
      
      // Save to localStorage
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('token', response.token);
      persistRoles(companyAdmin, systemAdmin);
      
      return { success: true, data: response };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    setIsCompanyAdmin(false);
    setIsSystemAdmin(false);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('isCompanyAdmin');
    localStorage.removeItem('isSystemAdmin');
  };

  const refreshUser = () => {
    // Re-read user data from localStorage after 2FA verification
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    const storedCompanyAdmin = localStorage.getItem('isCompanyAdmin');
    const storedSystemAdmin = localStorage.getItem('isSystemAdmin');
    
    if (storedUser && storedToken) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && typeof parsedUser === 'object' && parsedUser.id) {
          const companyAdmin =
            asBool(storedCompanyAdmin) || asBool(parsedUser.is_company_admin);
          const systemAdmin =
            asBool(storedSystemAdmin) || asBool(parsedUser.is_system_admin);
          setUser(parsedUser);
          setIsAuthenticated(true);
          setIsCompanyAdmin(companyAdmin);
          setIsSystemAdmin(systemAdmin);
          persistRoles(companyAdmin, systemAdmin);
        }
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      isCompanyAdmin, 
      isSystemAdmin,
      isLoading,
      signup, 
      login, 
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
